<script setup lang="ts">
import { ref } from "vue";
import { v4 as uuidv4 } from "uuid";
import { Action } from "@/modules/rule_engine/ruleEngineClasses";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import ActionRow from "@/modules/rule_engine/components/ActionRow.vue";

const actions = defineModel<Action[]>({ required: true });

const store = useRuleEnginedStore();

/** Accordion: opening an action closes the one already open. */
const expandedUuid = ref<string | null>(null);

const isExpanded = (uuid: string) => expandedUuid.value === uuid;

const toggle = (uuid: string) => {
	expandedUuid.value = expandedUuid.value === uuid ? null : uuid;
};

/** Pricing models modify output variables; other models run direct actions. */
const createAction = (): Action => {
	if (store.availableActionVariables.length > 0) {
		return {
			uuid: uuidv4(),
			type: "modify_percentage",
			variable: store.availableActionVariables[0].name,
			amount: 0.1,
		};
	}

	const firstTask =
		store.ruleEngineState.values_for_action_type
			.add_task_to_bex_reservation?.[0];

	return {
		uuid: uuidv4(),
		type: "add_task_to_bex_reservation",
		task_id: firstTask?.uuid ?? "",
	};
};

const addAction = () => {
	const action = createAction();

	actions.value.push(action);
	expandedUuid.value = action.uuid;
};

const removeAction = (uuid: string) => {
	const index = actions.value.findIndex((action) => action.uuid === uuid);
	if (index !== -1) actions.value.splice(index, 1);

	if (expandedUuid.value === uuid) expandedUuid.value = null;
};
</script>

<template>
	<div>
		<v-card class="pa-4" variant="elevated">
			<div v-if="actions.length === 0" class="empty">
				<v-icon size="16" color="warning" class="mr-2">
					mdi-alert-circle-outline
				</v-icon>
				<i>Add at least one action — this rule cannot be saved without one.</i>
			</div>

			<action-row
				v-for="(action, index) in actions"
				:key="action.uuid"
				v-model="actions[index]"
				:index="index"
				:expanded="isExpanded(action.uuid)"
				@toggle="toggle(action.uuid)"
				@remove="removeAction(action.uuid)"
			/>

			<v-btn
				class="mt-3"
				variant="text"
				color="#e67e22"
				prepend-icon="mdi-plus"
				@click="addAction"
			>
				Add action
			</v-btn>
		</v-card>
	</div>
</template>

<style scoped lang="scss">
.empty {
	display: flex;
	align-items: center;
	opacity: 0.85;
}
</style>
